<template>
  <div class="rounded-2xl bg-white p-5 shadow-sm">
    <div class="flex items-start gap-2 mb-1">
      <span class="text-lg">📊</span>
      <h3 class="text-lg font-bold text-gray-800">Rendimiento Detallado por Categoría</h3>
    </div>
    <p class="text-sm text-gray-500 mb-4">Detalle de precisión y progreso por categoría.</p>

    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <div class="overflow-x-auto rounded-xl bg-white">
          <b-skeleton-table
            animation="wave"
            :rows="5"
            :columns="4"
            :table-props="{ bordered: false, striped: false }"
          ></b-skeleton-table>
        </div>
      </template>
      <div v-if="Object.keys(processedCategories).length" class="overflow-x-auto rounded-xl bg-white">
        <table class="min-w-full divide-y divide-gray-100">
        <thead class="bg-gray-50/60">
          <tr>
            <th
              scope="col"
              class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              Categoría
            </th>
            <th
              scope="col"
              class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              Precisión
              <span class="ml-1 text-gray-300" title="Porcentaje de respuestas correctas">?</span>
            </th>
            <th
              scope="col"
              class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              Progreso
              <span class="ml-1 text-gray-300" title="Progreso sobre el total de preguntas">?</span>
            </th>
            <th
              scope="col"
              class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"
            >
              Intentadas
              <span class="ml-1 text-gray-300" title="Respondidas / Total">?</span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <template v-for="(categoryData, categoryName) in processedCategories">
            <tr :key="categoryName" class="transition-colors duration-200 hover:bg-gray-50/70">
              <td
                class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
              >
                <button
                  @click="toggleSubcategories(categoryName)"
                  class="flex items-center text-gray-700 hover:text-gray-900 focus:outline-none"
                  v-if="hasSubcategories(categoryName)"
                >
                  <svg
                    v-if="expandedCategories[categoryName]"
                    class="h-4 w-4 transform rotate-90 mr-2 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  <svg
                    v-else
                    class="h-4 w-4 mr-2 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                  {{ categoryName }}
                </button>
                <span v-else>{{ categoryName }}</span>
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                <span
                  :class="getPrecisionColor(calculatePrecision(categoryData))"
                >
                  {{ calculatePrecision(categoryData) }}%
                </span>
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div class="flex items-center gap-2">
                  <div class="w-28 bg-gray-100 rounded-full h-2">
                    <div
                      class="bg-blue-500 h-2 rounded-full"
                      :style="{ width: calculateProgress(categoryData) + '%' }"
                    ></div>
                  </div>
                  <span class="text-xs text-gray-500 tabular-nums">
                    {{ calculateProgress(categoryData) }}%
                  </span>
                </div>
              </td>

              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right tabular-nums">
                {{ categoryData.correctas + categoryData.incorrectas }} /
                {{ categoryData.total }}
              </td>
            </tr>

            <tr
              v-if="expandedCategories[categoryName]"
              :key="categoryName + '-sub'"
              class="bg-gray-50/70"
            >
              <td :colspan="4" class="p-0">
                <div class="bg-gray-50 p-4">
                  <h4 class="text-sm font-semibold text-gray-700 mb-2">
                    Subcategorías de {{ categoryName }}
                  </h4>

                  <ul class="list-disc pl-5 space-y-1">
                    <li
                      v-for="(sub, subIndex) in getSubcategories(categoryName)"
                      :key="subIndex"
                      class="text-sm text-gray-600"
                    >
                      {{ sub.subcategory }}

                      <span
                        class="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                        >(Prog: 15%)</span
                      >
                      <span
                        class="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700"
                        >(Prec: 90%)</span
                      >
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <div v-else class="rounded-xl bg-gray-50 p-6 text-sm text-gray-500">
      No hay datos disponibles para mostrar todavía.
    </div>
    </b-skeleton-wrapper>
  </div>
</template>

<script>
import { getCategoryStats } from "@/services/stats.service";

export default {
  name: "CategoryDetailedPerformance",

  data() {
    return {
      loading: true,
      rawCategoriesData: {},
      rawSubcategoriesData: [],
      expandedCategories: {},
    };
  },

  props: {
    isPremium: {
      type: Boolean,
      default: true
    },
    forStudentId: {
      type: [Number, String],
      default: null
    }
  },

  watch: {
    isPremium(newValue) {
      if (newValue) {
        this.loading = true;
        this.fetchCategoryStats();
      } else if (newValue === false) {
        this.loading = false;
        this.rawCategoriesData = {};
        this.rawSubcategoriesData = [];
      }
    },
    forStudentId() {
      if (this.isPremium) {
        this.loading = true;
        this.fetchCategoryStats();
      }
    }
  },
  
  created() {
    // Si no es premium, mantener loading indefinidamente y no cargar datos
    if (this.isPremium === false) {
      this.loading = false;
      return;
    }

    if (this.isPremium) {
      this.fetchCategoryStats();
    }
  },

  mounted() {
    // Si no es premium, mantener skeleton indefinidamente
    if (this.isPremium === false) {
      this.loading = false;
    }
  },

  computed: {
    processedCategories() {
      const cleanedData = {};
      for (const key in this.rawCategoriesData) {
        if (
          Object.prototype.hasOwnProperty.call(this.rawCategoriesData, key) &&
          key !== "generalStats"
        ) {
          cleanedData[key.trim()] = this.rawCategoriesData[key];
        }
      }
      return cleanedData;
    },
  },

  methods: {
    async fetchCategoryStats() {
      if (!this.isPremium) {
        return;
      }

      try {
        const response = await getCategoryStats(this.forStudentId);
        if (!response) {
          this.rawCategoriesData = {};
          this.rawSubcategoriesData = [];
          this.loading = false;
          return;
        }
        this.rawCategoriesData = response;
        this.loading = false; // Ocultar skeleton inmediatamente después de cargar
      } catch (error) {
        console.error("Error fetching category stats:", error);
        this.loading = false; // Ocultar skeleton incluso si hay error
      }
    },
    calculatePrecision(data) {
      const { correctas, incorrectas } = data;
      if (correctas + incorrectas === 0) return 0;
      return ((correctas / (correctas + incorrectas)) * 100).toFixed(0);
    },
    calculateProgress(data) {
      const { correctas, incorrectas, total } = data;
      if (total === 0) return 0;
      return (((correctas + incorrectas) / total) * 100).toFixed(0);
    },
    getPrecisionColor(precision) {
      if (precision > 70) return "font-bold text-emerald-600";
      if (precision >= 40) return "font-medium text-amber-600";
      return "font-medium text-red-600";
    },
    hasSubcategories(categoryName) {
      return this.rawSubcategoriesData.some(
        (sub) => sub.category.trim() === categoryName.trim()
      );
    },
    getSubcategories(categoryName) {
      return this.rawSubcategoriesData.filter(
        (sub) => sub.category.trim() === categoryName.trim()
      );
    },
    toggleSubcategories(categoryName) {
      this.$set(
        this.expandedCategories,
        categoryName,
        !this.expandedCategories[categoryName]
      );
    },
  },
};
</script>
